import * as React from 'react';
import {
  Card,
  Button,
  Label,
  RadioGroup,
  RadioGroupItem,
} from '@tinct/web-components';
import { cn } from '@tinct/core-utils';
import type { AnalyticsMode } from '@tinct/core-api-models';

type AnalyticsModeOption = {
  value: AnalyticsMode;
  title: string;
  description: string;
};

const ANALYTICS_MODE_OPTIONS: AnalyticsModeOption[] = [
  {
    value: 'basic',
    title: 'Basic: no cookie stored.',
    description:
      'Every visit counts as a unique visitor, so no returning-user tracking, but also no consent banner needed.',
  },
  {
    value: 'advanced',
    title: 'Advanced',
    description:
      'Requires visitor consent for a Tinct cookie, in exchange for proper returning-user tracking.',
  },
];

export type SnippetAnalyticsModeCardProps = {
  className?: string;
  value?: AnalyticsMode | null;
  isSaving?: boolean;
  onSave?: (mode: AnalyticsMode) => void;
};

export function SnippetAnalyticsModeCard({
  className,
  value,
  isSaving = false,
  onSave,
}: SnippetAnalyticsModeCardProps) {
  const initialMode: AnalyticsMode = value ?? 'basic';
  const [selectedMode, setSelectedMode] =
    React.useState<AnalyticsMode>(initialMode);

  // Sync local selection when the persisted value changes (e.g. after refetch).
  React.useEffect(() => {
    setSelectedMode(initialMode);
  }, [initialMode]);

  const isDirty = selectedMode !== initialMode;

  const handleSave = () => {
    if (!isDirty) return;
    onSave?.(selectedMode);
  };

  return (
    <Card className={cn('gap-0 py-0', className)}>
      {/* Header */}
      <div className="px-6 pt-6">
        <h3 className="font-heading text-xl font-semibold leading-7 tracking-[-0.3px] text-foreground">
          Analytics mode
        </h3>
      </div>

      {/* Options */}
      <div className="px-6 py-6">
        <RadioGroup
          value={selectedMode}
          onValueChange={(value) => setSelectedMode(value as AnalyticsMode)}
          className="gap-4"
        >
          {ANALYTICS_MODE_OPTIONS.map((option) => (
            <div key={option.value} className="flex items-start gap-3">
              <RadioGroupItem
                value={option.value}
                id={`analytics-mode-${option.value}`}
                className="mt-0.5"
              />
              <div className="flex flex-col gap-1">
                <Label
                  htmlFor={`analytics-mode-${option.value}`}
                  className="text-sm font-semibold text-foreground"
                >
                  {option.title}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {option.description}
                </p>
              </div>
            </div>
          ))}
        </RadioGroup>
      </div>

      {/* Footer */}
      <div className="flex justify-end border-t px-6 py-4">
        <Button
          onClick={handleSave}
          disabled={!isDirty || isSaving}
          isLoading={isSaving}
        >
          Save
        </Button>
      </div>
    </Card>
  );
}
